/**
 * Walidatory Zod (v4) współdzielone FE/BE (§6.3) + czyste funkcje sum kontrolnych.
 * Komunikaty błędów to KLUCZE i18n (etykiety UI wyłącznie w @k1p4/i18n).
 */

import { z } from 'zod'
import { PASSWORD_MIN_LENGTH } from './constants'

// ---------------------------------------------------------------------------
// PESEL — 11 cyfr, suma kontrolna (wagi 1,3,7,9,1,3,7,9,1,3) + zakodowana data
// ---------------------------------------------------------------------------

const PESEL_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3] as const

/**
 * Stulecia zakodowane w miesiącu PESEL: przesunięcie miesiąca +20 na każde
 * stulecie od 1900 (81–92 → lata 1800).
 */
const PESEL_CENTURY_BY_MONTH_OFFSET: Record<number, number> = {
  0: 1900,
  20: 2000,
  40: 2100,
  60: 2200,
  80: 1800,
}

function daysInMonth(year: number, month: number): number {
  // month 1..12; new Date(y, m, 0) zwraca ostatni dzień miesiąca m
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export interface PeselBirthDate {
  readonly year: number
  readonly month: number
  readonly day: number
}

/** Dekoduje datę urodzenia z PESEL (z obsługą stuleci); null, gdy data nieprawidłowa. */
export function decodePeselBirthDate(pesel: string): PeselBirthDate | null {
  if (!/^\d{11}$/.test(pesel)) return null
  const yy = Number(pesel.slice(0, 2))
  const encodedMonth = Number(pesel.slice(2, 4))
  const day = Number(pesel.slice(4, 6))
  const offset = encodedMonth - ((encodedMonth - 1) % 20) - 1
  const century = PESEL_CENTURY_BY_MONTH_OFFSET[offset]
  if (century === undefined) return null
  const month = encodedMonth - offset
  if (month < 1 || month > 12) return null
  const year = century + yy
  if (day < 1 || day > daysInMonth(year, month)) return null
  return { year, month, day }
}

export function isValidPesel(value: string): boolean {
  if (!/^\d{11}$/.test(value)) return false
  let sum = 0
  for (let i = 0; i < 10; i += 1) {
    sum += (value.charCodeAt(i) - 48) * PESEL_WEIGHTS[i]!
  }
  const control = (10 - (sum % 10)) % 10
  if (control !== value.charCodeAt(10) - 48) return false
  return decodePeselBirthDate(value) !== null
}

export const peselSchema = z
  .string()
  .trim()
  .refine(isValidPesel, { error: 'validation.pesel.invalid' })

// ---------------------------------------------------------------------------
// NIP — 10 cyfr (akceptowany format z kreskami/spacjami), wagi 6,5,7,2,3,4,5,6,7
// ---------------------------------------------------------------------------

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7] as const

export function normalizeNip(value: string): string {
  return value.replace(/[\s-]/g, '')
}

export function isValidNip(value: string): boolean {
  const nip = normalizeNip(value)
  if (!/^\d{10}$/.test(nip)) return false
  let sum = 0
  for (let i = 0; i < 9; i += 1) {
    sum += (nip.charCodeAt(i) - 48) * NIP_WEIGHTS[i]!
  }
  const control = sum % 11
  // control === 10 nie może wystąpić w poprawnym NIP (cyfra kontrolna to 0..9)
  return control === nip.charCodeAt(9) - 48
}

/** Wyjście znormalizowane: same cyfry (bez kresek/spacji). */
export const nipSchema = z
  .string()
  .transform(normalizeNip)
  .refine(isValidNip, { error: 'validation.nip.invalid' })

// ---------------------------------------------------------------------------
// REGON — 9 albo 14 cyfr, suma kontrolna mod-11 (identyfikacja płatnika w KEDU)
// ---------------------------------------------------------------------------

const REGON_9_WEIGHTS = [8, 9, 2, 3, 4, 5, 6, 7] as const
const REGON_14_WEIGHTS = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8] as const

export function normalizeRegon(value: string): string {
  return value.replace(/[\s-]/g, '')
}

/** Cyfra kontrolna REGON: suma iloczynów mod 11, gdzie reszta 10 znaczy 0. */
function regonChecksum(digits: string, weights: readonly number[]): number {
  let sum = 0
  for (const [index, weight] of weights.entries()) {
    sum += (digits.charCodeAt(index) - 48) * weight
  }
  const control = sum % 11
  return control === 10 ? 0 : control
}

export function isValidRegon(value: string): boolean {
  const regon = normalizeRegon(value)
  if (!/^\d{9}$/.test(regon) && !/^\d{14}$/.test(regon)) return false
  const nine = regonChecksum(regon, REGON_9_WEIGHTS) === regon.charCodeAt(8) - 48
  if (regon.length === 9) return nine
  /* REGON 14-znakowy niesie w pierwszych 9 znakach poprawny REGON 9-znakowy. */
  return nine && regonChecksum(regon, REGON_14_WEIGHTS) === regon.charCodeAt(13) - 48
}

export const regonSchema = z
  .string()
  .transform(normalizeRegon)
  .refine(isValidRegon, { error: 'validation.regon.invalid' })

// ---------------------------------------------------------------------------
// IBAN PL — „PL" + 26 cyfr, suma kontrolna mod-97 (ISO 13616)
// ---------------------------------------------------------------------------

/**
 * Numer rachunku do postaci IBAN PL. NRB to IBAN PL bez kodu kraju — w tej
 * postaci ZUS nadaje rachunek e-składki — więc 26 cyfr dostaje „PL" (grupa 83).
 */
export function normalizeIbanPl(value: string): string {
  const compact = value.replace(/\s/g, '').toUpperCase()
  return /^\d{26}$/.test(compact) ? `PL${compact}` : compact
}

function mod97(numeric: string): number {
  let remainder = 0
  for (let i = 0; i < numeric.length; i += 1) {
    remainder = (remainder * 10 + (numeric.charCodeAt(i) - 48)) % 97
  }
  return remainder
}

export function isValidIbanPl(value: string): boolean {
  const iban = normalizeIbanPl(value)
  if (!/^PL\d{26}$/.test(iban)) return false
  // Przestawienie pierwszych 4 znaków na koniec; litery jako liczby: P=25, L=21
  const rearranged = iban.slice(4) + '2521' + iban.slice(2, 4)
  return mod97(rearranged) === 1
}

/** Wyjście znormalizowane: bez spacji, wielkie litery (PL + 26 cyfr). */
export const ibanPlSchema = z
  .string()
  .transform(normalizeIbanPl)
  .refine(isValidIbanPl, { error: 'validation.iban.invalid' })

// ---------------------------------------------------------------------------
// Pozostałe formaty
// ---------------------------------------------------------------------------

export const postalCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{2}-\d{3}$/, { error: 'validation.postalCode.invalid' })

/** Telefon PL: 9 cyfr, opcjonalny prefiks +48; wyjście znormalizowane do 9 cyfr. */
export function normalizePhonePl(value: string): string {
  const compact = value.replace(/[\s-]/g, '')
  return compact.startsWith('+48') ? compact.slice(3) : compact
}

export const phonePlSchema = z
  .string()
  .transform(normalizePhonePl)
  .refine((v) => /^\d{9}$/.test(v), { error: 'validation.phone.invalid' })

/** Kod zaproszenia XXXX-XXXX-XXXX (A–Z, 0–9); wyjście znormalizowane do wielkich liter. */
export const invitationCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/, { error: 'validation.invitationCode.invalid' })

/** Numer zaproszenia INV-XXXXX (A–Z, 0–9); wyjście znormalizowane do wielkich liter. */
export const invNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^INV-[A-Z0-9]{5}$/, { error: 'validation.invNumber.invalid' })

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { error: 'validation.password.tooShort' })

/** E-mail znormalizowany do małych liter (e-mail jest unikalny globalnie [D13]). */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ error: 'validation.email.invalid' }))
